import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'
import { toast } from 'sonner'
import { Camera, X, Loader2 } from '@/components/icons'
import { Card } from './ui/card'
import { ImageGrid, type GridImage } from './ImageGrid'
import {
  useS3GetUploadPost,
  useS3DownloadUrlQuery,
  useS3DownloadUrlsQuery,
  uploadThumbnailBestEffort,
  generateTaskPhotoS3Key,
} from '@/lib/hooks/s3'
import { useTaskAddPhoto, useTaskRemovePhoto } from '@/lib/hooks/task'
import { isNative, openWithNativeViewer } from '@/lib/native-file-viewer'
import { cn } from '@/lib/utils'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const ACCEPT = 'image/*'

export function fileNameFromS3Key(s3Key: string): string {
  const last = s3Key.split('/').pop() || s3Key
  return last || 'photo'
}

/** Compact round camera button: opens the file picker (design's icon-only
    trigger, used where the big AddPhotoTile square would be too heavy). */
export function AddPhotoIconButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add photo"
      className="size-8 rounded-full bg-content-brand-subtle flex items-center justify-center text-content-brand-bold transition-opacity hover:opacity-80"
    >
      <Camera className="size-4" />
    </button>
  )
}

/** Trailing tile of a photo grid: opens the file picker. */
export function AddPhotoTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add photo"
      className="aspect-square rounded-lg border border-border-L1 bg-surface-L2 flex items-center justify-center text-content-brand-bold transition-colors hover:bg-surface-L1"
    >
      <Camera className="size-5" />
    </button>
  )
}

interface TaskPhotosProps {
  taskId: string
  photoS3Keys: string[]
  readOnly?: boolean
  onPendingChange?: (count: number) => void
}

export interface TaskPhotosHandle {
  flushPending: (onUploaded?: () => void) => Promise<void>
  pendingUploadCount: () => number
}

interface PendingPhoto {
  id: string
  file: File
  previewUrl: string
}

export const TaskPhotos = forwardRef<TaskPhotosHandle, TaskPhotosProps>(
  function TaskPhotos({ taskId, photoS3Keys, readOnly = false, onPendingChange }, ref) {
    const getUploadPost = useS3GetUploadPost()
    const addPhoto = useTaskAddPhoto()
    const removePhoto = useTaskRemovePhoto()

    const fileInputRef = useRef<HTMLInputElement>(null)
    const [pending, setPending] = useState<PendingPhoto[]>([])
    const [pendingRemovals, setPendingRemovals] = useState<string[]>([])
    const [uploadingIds, setUploadingIds] = useState<Set<string>>(new Set())

    const visibleS3Keys = photoS3Keys.filter((k) => !pendingRemovals.includes(k))

    // Read-only collage: cached/deduped per key; tiles get the 400px variant,
    // the lightbox the original.
    const { urls, isPending: urlsLoading } = useS3DownloadUrlsQuery(
      readOnly ? visibleS3Keys : [],
      { variant: 'thumb' }
    )


    useEffect(() => {
      onPendingChange?.(pending.length + pendingRemovals.length)
    }, [pending.length, pendingRemovals.length, onPendingChange])

    useEffect(() => {
      return () => {
        // Revoke any outstanding object URLs on unmount
        setPending((cur) => {
          cur.forEach((p) => URL.revokeObjectURL(p.previewUrl))
          return cur
        })
      }
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        pendingUploadCount: () => pending.length,
        flushPending: async (onUploaded) => {
          if (pending.length === 0 && pendingRemovals.length === 0) return
          const uploadedIds = new Set<string>()
          const removedKeys = new Set<string>()
          let firstError: unknown = null

          for (const item of pending) {
            setUploadingIds((cur) => new Set(cur).add(item.id))
            try {
              const s3Key = generateTaskPhotoS3Key(taskId, item.file.name)
              const { url, fields } = await getUploadPost.mutateAsync({
                s3Key,
                contentType: item.file.type || 'application/octet-stream',
              })

              const formData = new FormData()
              Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
              formData.append('file', item.file)

              const res = await fetch(url, { method: 'POST', body: formData })
              if (!res.ok) throw new Error(`Upload failed (${res.status})`)

              await uploadThumbnailBestEffort(item.file, s3Key, getUploadPost.mutateAsync)

              await addPhoto.mutateAsync({ id: taskId, s3Key })
              uploadedIds.add(item.id)
              URL.revokeObjectURL(item.previewUrl)
              onUploaded?.()
            } catch (err) {
              if (!firstError) firstError = err
              const msg = err instanceof Error ? err.message : 'Upload failed'
              toast.error(`${item.file.name}: ${msg}`)
            } finally {
              setUploadingIds((cur) => {
                const next = new Set(cur)
                next.delete(item.id)
                return next
              })
            }
          }

          for (const s3Key of pendingRemovals) {
            try {
              await removePhoto.mutateAsync({ id: taskId, s3Key })
              removedKeys.add(s3Key)
            } catch (err) {
              if (!firstError) firstError = err
              const msg = err instanceof Error ? err.message : 'Failed to remove photo'
              toast.error(msg)
            }
          }

          setPending((cur) => cur.filter((p) => !uploadedIds.has(p.id)))
          setPendingRemovals((cur) => cur.filter((k) => !removedKeys.has(k)))
          if (firstError) throw firstError
        },
      }),
      [pending, pendingRemovals, taskId, getUploadPost, addPhoto, removePhoto]
    )

    if (readOnly && visibleS3Keys.length === 0 && pending.length === 0) return null

    const handlePickFiles = () => {
      fileInputRef.current?.click()
    }

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      e.target.value = ''
      if (files.length === 0) return

      const accepted: PendingPhoto[] = []
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`)
          continue
        }
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`${file.name} exceeds the 20 MB limit`)
          continue
        }
        accepted.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          previewUrl: URL.createObjectURL(file),
        })
      }
      if (accepted.length > 0) {
        setPending((cur) => [...cur, ...accepted])
      }
    }

    const handleRemoveUploaded = (s3Key: string) => {
      setPendingRemovals((cur) => (cur.includes(s3Key) ? cur : [...cur, s3Key]))
    }

    const handleRemovePending = (id: string) => {
      setPending((cur) => {
        const next = cur.filter((p) => p.id !== id)
        const removed = cur.find((p) => p.id === id)
        if (removed) URL.revokeObjectURL(removed.previewUrl)
        return next
      })
    }

    const gridImages: GridImage[] = visibleS3Keys.map((k) => ({
      id: k,
      url: urls[k]?.downloadUrl ?? null,
      thumbUrl: urls[k]?.thumbnailUrl ?? null,
      name: fileNameFromS3Key(k),
    }))

    // Compact icon trigger above the grid (design's icon-only trigger); the
    // grid is skipped entirely while empty so no bare box shows.
    const editableBody = (
      <div className="flex flex-col gap-2">
        <AddPhotoIconButton onClick={handlePickFiles} />
        {(visibleS3Keys.length > 0 || pending.length > 0) && (
          <div className="grid grid-cols-3 gap-2">
            {visibleS3Keys.map((s3Key) => (
              <PhotoThumbnail
                key={s3Key}
                s3Key={s3Key}
                onRemove={() => handleRemoveUploaded(s3Key)}
              />
            ))}
            {pending.map((p) => (
              <PendingThumbnail
                key={p.id}
                previewUrl={p.previewUrl}
                uploading={uploadingIds.has(p.id)}
                onRemove={() => handleRemovePending(p.id)}
              />
            ))}
          </div>
        )}
      </div>
    )

    return (
      <Card className="flex flex-col gap-2 border-border-L1">
        <p className="text-sm font-semibold text-content-primary-bold leading-5">Photos</p>
        {readOnly ? (
          <ImageGrid images={gridImages} loading={urlsLoading} />
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={handleFilesSelected}
            />
            {isNative ? editableBody : <PhotoProvider>{editableBody}</PhotoProvider>}
          </>
        )}
      </Card>
    )
  }
)

export function PendingThumbnail({
  previewUrl,
  uploading = false,
  onRemove,
}: {
  previewUrl: string
  uploading?: boolean
  onRemove: () => void
}) {
  const thumbnail = (
    <img
      src={previewUrl}
      alt=""
      className="w-full aspect-square object-cover rounded-lg opacity-80"
    />
  )

  return (
    <div className="relative">
      {isNative || uploading ? (
        thumbnail
      ) : (
        <PhotoView src={previewUrl}>{thumbnail}</PhotoView>
      )}
      {uploading ? (
        <div className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center">
          <Loader2 className="size-5 text-white animate-spin" />
        </div>
      ) : (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 size-5 rounded-full bg-surface-L0/90 flex items-center justify-center shadow-sm"
        >
          <X className="size-3 text-content-primary-bold" />
        </button>
      )}
    </div>
  )
}

export function PhotoThumbnail({
  s3Key,
  localUrl,
  onRemove,
}: {
  s3Key: string
  // Local object URL for a just-uploaded photo — renders it directly instead
  // of fetching a presigned download URL for an image already in memory.
  localUrl?: string
  // Omit to render the thumbnail without a remove affordance (read-only).
  onRemove?: () => void
}) {
  // Cached + deduped per key across the app, so a grid of N tiles is N
  // requests once (not per mount) and the URL is re-signed before it expires.
  // The tile shows the 400px thumbnail uploaded next to the photo (or the
  // original for photos that have none); the lightbox / native viewer always
  // gets the original.
  const { data: rawData, isPending } = useS3DownloadUrlQuery(localUrl ? null : s3Key, {
    variant: 'thumb',
  })
  // Same cast as ProfileAvatar: the oRPC/TanStack query typing resolves to
  // `unknown` in this app.
  const data = rawData as { downloadUrl?: string; thumbnailUrl?: string } | undefined
  const loading = !localUrl && isPending

  const fullUrl = localUrl ?? data?.downloadUrl ?? null
  const tileUrl = localUrl ?? data?.thumbnailUrl ?? data?.downloadUrl ?? null

  const handleNativeTap = async () => {
    if (!fullUrl) return
    await openWithNativeViewer(fullUrl, fileNameFromS3Key(s3Key))
  }

  const thumbnail = tileUrl ? (
    <img
      src={tileUrl}
      alt=""
      loading="lazy"
      decoding="async"
      className="w-full aspect-square object-cover rounded-lg cursor-pointer"
    />
  ) : (
    <div
      className={cn(
        'w-full aspect-square rounded-lg bg-surface-L2 flex items-center justify-center',
        loading && 'animate-pulse'
      )}
    />
  )

  return (
    <div className="relative">
      {isNative ? (
        <button type="button" onClick={handleNativeTap} className="block w-full">
          {thumbnail}
        </button>
      ) : fullUrl ? (
        <PhotoView src={fullUrl}>{thumbnail}</PhotoView>
      ) : (
        thumbnail
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 size-5 rounded-full bg-surface-L0/90 flex items-center justify-center shadow-sm"
        >
          <X className="size-3 text-content-primary-bold" />
        </button>
      )}
    </div>
  )
}
